

















































































































import {
  defineComponent,
  ref,
  computed,
  useRouter,
  useRoute,
  watch,
  useFetch,
  useContext,
  onMounted,
  onUnmounted,
  onUpdated,
} from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import { debounce } from 'lodash-es';
import urlJoin from 'url-join';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import AppSeeMoreButton from '~/components/partials/App/AppSeeMoreButton.vue';
import ContentDynamicComponent from '~/components/partials/Content/ContentDynamicComponent.vue';
import Producer from '~/components/partials/Content/ContentProducer.vue';
import ContentProducerSideMenu from '~/components/partials/Content/ContentProducerSideMenu.vue';
import ContentProductList from '~/components/partials/Content/ContentProductList.vue';
import ContentRelatedContents from '~/components/partials/Content/ContentRelatedContents.vue';
import ContentRelatedLinks from '~/components/partials/Content/ContentRelatedLinks.vue';
import ContentTagsAndShareIcons from '~/components/partials/Content/ContentTagsAndShareIcons.vue';
import ContentLayout from '~/components/partials/Layout/ContentLayout.vue';
import { useContentParts, ContentParts } from '~/composables/contents/useContentParts';
import { useListFavorite } from '~/composables/contents/useListFavorite';
import { useRequestBody } from '~/composables/contents/useRequestBody';
import { useSearchProductsParam } from '~/composables/contents/useSearchProductsParam';
import { useKeepScroll } from '~/composables/useKeepScroll';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useRuntimeConfig } from '~/composables/useRuntimeConfig';
import { useStore } from '~/composables/useStore';
import { useWOVNio } from '~/composables/wovn/useWOVNio';
import {
  PRODUCT_LIST_PER_PAGE,
  PRODUCT_SEARCH_SORT_OPTIONS,
  PRODUCT_PARAM_QUERIES,
  DYNAMIC_PARTS_NAME,
} from '~/constants';
import { BOTTLE_250, BOTTLE_400 } from '~/constants/profilesForImage';
import { url, karteTrack, nonNullable } from '~/utils';
import { PageError } from '~/utils/error';
import { CommonProducingArea } from '~/types/common';
import type { ContentProducer } from '~/types/modules/contents';
import { ProductListItem } from '~/types/modules/product';
import { ArticleType, DisplayLang, FavoriteTypes } from '~/enums';

export default defineComponent({
  components: {
    ContentTagsAndShareIcons,
    ContentDynamicComponent,
    AppLoader,
    AppImage,
    AppSeeMoreButton,
    ContentProducerSideMenu,
    Producer,
    ContentLayout,
    ContentProductList,
    ContentRelatedContents,
    ContentRelatedLinks,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { FRONTEND_ORIGIN } = useRuntimeConfig();
    const data = ref<any>();
    const { createQuery, createSortParam } = useSearchProductsParam();
    const { parseRequestBody } = useRequestBody();
    const { fetchAllFavorites } = useListFavorite();
    const { currentLang } = useWOVNio();
    const { fetchContentParts } = useContentParts();
    const { i18n, req } = useContext();
    const { scrollToPreviousPosition, setPageData } = useKeepScroll();

    // 商品データ
    const products = ref<Array<ProductListItem>>([]);
    // 在庫あるかどうか
    const hasStock = ref(store.getters['internals/keepScroll/hasStock'](route.value.path, route.value.fullPath));
    // 商品一覧項目のindex
    const currentSortIndex = ref(
      store.getters['internals/keepScroll/sortIndex'](route.value.path, route.value.fullPath)
    );
    // 商品一覧項目のページネーション
    const pagination = ref(store.getters['internals/keepScroll/pagination'](route.value.path, route.value.fullPath, 0));
    // 商品一覧の合計値
    const totalNumber = ref(0);
    // 生産者の生産地
    const producer = ref<ContentProducer | null>(null);
    // 関連コンテンツなどのパーツ
    const parts = ref<ContentParts>();
    // ダイナミックパーツのDOM参照
    const dynamicPartsWrapperRef = ref<HTMLDivElement | null>(null);
    // プレビュー表示かどうか（管理画面経由）
    const isPreview = ref<boolean>(req && req.method === 'POST');

    // _producers クエリがあったら除去した canonical を返す
    const canonical = computed((): string | undefined => {
      const url = new URL(route.value.fullPath, FRONTEND_ORIGIN);

      if (url.searchParams.has(PRODUCT_PARAM_QUERIES.PRODUCER)) {
        url.searchParams.delete(PRODUCT_PARAM_QUERIES.PRODUCER);

        return url.toString().replace(FRONTEND_ORIGIN, '');
      }
    });

    const isItemsReady = computed<boolean>(() => totalNumber.value === 0 || products.value.length > 0);

    const { fetchState } = useFetch(async () => {
      let producerId = '';
      let previewData: any = null;
      // プレビューの表示
      if (isPreview.value) {
        const reqBody = await parseRequestBody(req);
        if (!reqBody) throw new PageError(404, {});

        previewData = reqBody;
        producerId = previewData.permalink;
      } else {
        producerId = route.value.params.id;
      }

      if (!producerId) throw new PageError(404, {});

      await Promise.all(
        [
          async () => {
            parts.value = await fetchContentParts(ArticleType.Producers, producerId, previewData);
            data.value = parts.value.data;
          },
          async () => {
            try {
              const res = await store.dispatch('modules/content/fetchProducer', {
                q: `mst:producer AND iid: producer_${producerId}`,
              });
              producer.value = res[0];
            } catch {
              throw new PageError(404, {});
            }
          },
        ].map((fn) => fn())
      );

      await fetchProducts();
    });

    /**
     * 商品一覧取得
     */
    const fetchProducts = async () => {
      const producerId = route.value.params.id;

      if (!producerId) return;

      const param = {
        producer_id: Number(producerId),
      };

      const { q } = await createQuery(param, true, hasStock.value);

      const option = PRODUCT_SEARCH_SORT_OPTIONS[currentSortIndex.value];

      if (!option) {
        return;
      }

      const { target, order } = option;

      const sort = createSortParam(target, order, 'sort_order2_i asc', ArticleType.Producers);

      const { items, total } = await store.dispatch('modules/product/fetchProducts', {
        q,
        sort,
        pagination: pagination.value,
      });

      products.value = items;
      totalNumber.value = total;
    };

    watch(
      [hasStock, currentSortIndex, pagination],
      debounce(async () => await fetchProducts(), 100)
    );

    // パンくず
    const breadcrumbs = computed(() => {
      if (!producer.value) return;

      return [
        { title: i18n.t('Content-07.listTitle').toString(), to: url('PRODUCER_LIST') },
        {
          title: currentLang.value.code === DisplayLang.JA ? producer.value.name : producer.value.nameEn,
          to: null,
        },
      ];
    });

    // もっと見るボタンの表示・非表示
    const isLoadMoreButtonVisible = computed(() => totalNumber.value > pagination.value * PRODUCT_LIST_PER_PAGE);

    /**
     * カテゴリ検索
     */
    const searchCategory = async (query: string) => {
      await router.push({
        path: url('PRODUCER_LIST'),
        query: {
          area: query,
        },
      });
    };

    /**
     * データ整形後の生産地
     */
    const producingAreas = computed((): CommonProducingArea[] => {
      if (!producer.value) return [{ code_id: '', areas: [] }];

      return producer.value.areas.map((area) => {
        return {
          code_id: area.id || '',
          areas: area.regions.map(({ id, name, enName }) => ({ id, name, enName })),
        };
      });
    });

    // karte
    const unsubscribe = watch(
      data,
      async (data) => {
        if (isPreview.value) return;
        if (process.server || !data) {
          return;
        }
        unsubscribe();

        const tags: string[] = [];
        const area1s: string[] = [];
        const area2s: string[] = [];
        const area3s: string[] = [];
        try {
          await Promise.all(
            [
              async () => {
                const { result } = await store.dispatch('modules/content/fetchTags', {
                  tags: data?.contents?.tags || [],
                });
                tags.push(...(result?.map((tagData) => tagData?.contents?.tag_name)?.filter(nonNullable) || []));
              },
              async () => {
                const res = await store.dispatch('modules/content/fetchProducer', {
                  q: `mst:producer AND iid: producer_${data.permalink}`,
                });
                area1s.push(...res[0].areas.map(({ regions }) => regions?.[0]?.name).filter(nonNullable));
                area2s.push(...res[0].areas.map(({ regions }) => regions?.[1]?.name).filter(nonNullable));
                area3s.push(...res[0].areas.map(({ regions }) => regions?.[2]?.name).filter(nonNullable));
              },
            ].map((fn) => fn())
          );
        } catch {
          // 何もしない
        }
        // KARTE
        karteTrack('_view_category', {
          category_num: totalNumber.value,
          category_id: `p${route.value.params.id}`,
          category_name: data?.contents?.name,
          category_url: urlJoin(FRONTEND_ORIGIN, route.value.fullPath),
          page_type: i18n.t('Content-07.kartePageType').toString(),
          open_from_dtime: dayjs(data?.publish_at || null).isValid()
            ? dayjs(data?.publish_at).tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          open_to_dtime: dayjs(data?.expire_at || null).isValid()
            ? dayjs(data?.expire_at).tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          area1: area1s.length > 0 ? area1s : undefined,
          area2: area2s.length > 0 ? area2s : undefined,
          area3: area3s.length > 0 ? area3s : undefined,
          producer: data?.contents?.name,
          tags,
        });
      },
      { immediate: true }
    );

    // noindexを適用するか
    const isEnabledNoIndex = computed((): boolean => {
      return !data.value?.contents.search_index;
    });

    onMounted(() => {
      // karteの送信処理には全件取得する必要があります
      fetchAllFavorites(FavoriteTypes.PRODUCER);
    });

    onUpdated(() => {
      if (dynamicPartsWrapperRef.value) {
        const scrollObserver = new MutationObserver(
          debounce((mutationsList, observer) => {
            scrollToPreviousPosition(500);
            observer.disconnect();
          }, 500)
        );
        scrollObserver.observe(dynamicPartsWrapperRef.value, { attributes: true, childList: true, subtree: true });
        dynamicPartsWrapperRef.value.id = 'dynamic-parts-wrapper'; // HACK: 変更を検知させるために属性を付与
      }
    });

    onUnmounted(() => {
      setPageData({
        pagination: [pagination.value], // 0:商品一覧（ContentProductList）
        detailsElements: [{ elementId: 'content-related-links' }], // 0:関連リンク
        hasStock: hasStock.value,
        sortIndex: currentSortIndex.value,
      });
    });

    useLayoutProps(() => ({
      layout: {
        headerMenu: 'never', // ContentLayoutの中で設置
      },
      head: {
        title: `${producer.value?.name} ${producer.value?.nameEn}`,
        description: data.value?.contents?.meta_description || producer.value?.description || undefined, // どちらも入力がない場合は、nullが返らないようにundefinedを入力
        canonical: canonical.value,
        noindex: isEnabledNoIndex.value,
      },
      og: {
        image: producer.value?.mainPcImage,
        description: data.value?.contents?.meta_description || producer.value?.description || undefined, // どちらも入力がない場合は、nullが返らないようにundefinedを入力
      },
      twitter: {
        card: 'summary_large_image',
      },
      breadcrumbs: breadcrumbs.value,
      fetchState: [fetchState],
      jsonld: [
        {
          type: 'BlogPosting',
          data: {
            url: url('PRODUCER_DETAIL', { id: producer.value?.id }),
            title: producer.value?.name || '',
            images: [producer.value?.mainPcImage || ''].filter(Boolean),
            publishedAt: data.value?.publish_at || '',
            updatedAt: data.value?.update_at,
            description: producer.value?.catchCopy,
          },
        },
      ],
    }));

    return {
      data,
      parts,
      fetchState,
      searchCategory,
      producingAreas,
      url,
      products,
      currentSortIndex,
      hasStock,
      pagination,
      totalNumber,
      isLoadMoreButtonVisible,
      producer,
      BOTTLE_250,
      BOTTLE_400,
      DYNAMIC_PARTS_NAME,
      isItemsReady,
      dynamicPartsWrapperRef,
    };
  },
  head: {},
});
